import React, { useEffect, useState } from "react";
import { Button } from "antd";
import {
  DeleteOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import PatientDetailsStageDocument from "../../../modals/PatientDetailsStageDocument";
import { IDocumentFields } from "../../../../interfaces/ICase";
import { AppDispatch } from "../../../../store/store";
import { getDeficiencyQuestionData } from "../../../../models/patientSlice";
import { errorMessage, successMessage } from "../../../../utils/notificationUtils";
import { downloadDocument } from "../../../../apis/patientService";

type RightSideFilePreviewProps = {
  documents: any;
  handleDeleteList?: any;
  activeKey: any;
  uploadedDocuments: any;
  setUploadedDocuments: any;
  isSubmitted?: boolean;
};

const RightSideFilePreview: React.FC<RightSideFilePreviewProps> = ({
  documents,
  handleDeleteList,
  activeKey,
  uploadedDocuments,
  setUploadedDocuments,
  isSubmitted
}) => {
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);
  const { auth, patients } = useSelector((state: any) => state);
  const [deficiencyModal, setDeficiencyModal] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);
  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] =
    useState<any>();
  const [previewDocument, setPreviewDocument] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const isPdf = selectedDoc?.documentUrl?.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    if (documents?.length > 0) {
      setSelectedDoc(documents[0]);
    }
  }, [documents]);

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [selectedDoc]);

  const fetchDeficiencyData = async (
      selectedDocument: any,
      selectedDocumentId: any,
    ) => {
      try {
        if (selectedDocument?.id) {
          const secondRes = await dispatch(
            getDeficiencyQuestionData(selectedDocument?.id),
          );
        }
      } catch (err) {
        errorMessage("Please, Try again after sometime");
        setSelectedDocument(null);
      }
    };
    const handleDeficiencyModalOpen = async (
      documentUrl: string,
      documentId: number,
      document: IDocumentFields,
    ) => {
      await fetchDeficiencyData(document, documentId);
      setDeficiencyModal(true);
      setSelectedDocument(document);
    };
    const closeDeficiencyModal = () => {
      setDeficiencyModal(false);
      setSelectedDocument(null);
    };
  
    const handleCancelPreview = () => {
      setPreviewDocument(false);
    };

    const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const handleDownloadDocument = async (fileName: string, url: string) => {
      try {
        await downloadDocument(fileName, url);
        successMessage("Document Downloaded Successfully");
      } catch (err: any) {
        errorMessage("Please, Try again later");
      }
    };

  return (
    <div className="custom-overlay" style={{ height: "100%" }}>
      <div className="right-panel" style={{ height: "100%" }}>
        <div className="doc-list" style={{ marginBottom: 10 }}>
          {documents?.map((doc: any, index: any) => (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div
                key={index}
                className={`doc-item ${
                  selectedDoc?.documentUrl === doc.documentUrl ? "active" : ""
                }`}
                onClick={() => setSelectedDoc(doc)}
              >
                {doc.documentName}
              </div>
              <div style={{display: "flex", alignItems: "center", gap: 20}}>
              {handleDeleteList && !isSubmitted ? (
                <div>
                  <DeleteOutlined
                    style={{ color: "red", cursor: "pointer" }}
                    onClick={() =>
                      handleDeleteList(doc.documentType, doc.documentUrl, index)
                    }
                  />
                </div>
              ) : (
                <></>
              )}

              {auth?.role?.role !== "FO_STAFF" ? (
                <div
                  key={index}
                  style={{
                    color: "#00549e",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => handleOpen(doc.documentLabelType, doc.documentUrl)}
                >
                  Mark Deficiency Query
                </div>
              ) : (
                <></>
              )}
              </div>
            </div>
          ))}
        </div>

        <div className="preview-section" style={{ height: "100%" }}>
          {selectedDoc && (
            <>
              {isPdf ? (
                <object
                  className="preview-content"
                  data={selectedDoc.documentUrl}
                  type="application/pdf"
                  style={{ height: "100%", width: "100%" }}
                >
                  <p>
                    Unable to display.{" "}
                    <a href={selectedDoc.documentUrl} target="_blank">
                      Open
                    </a>
                  </p>
                </object>
              ) : (
                <div className="image-container" style={{ height: "100%" }}>
                  <img
                    src={selectedDoc.documentUrl}
                    alt="preview"
                    style={{
                      transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                      transformOrigin: "top center",
                      transition: "transform 0.3s ease",
                      maxWidth: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              )}
            </>
          )}

          <div className="footer">
            {!isPdf && (
              <div className="zoom-controls">
                <Button
                  onClick={() => setZoomLevel((z) => Math.max(z - 0.1, 0.5))}
                >
                  <ZoomOutOutlined />
                </Button>
                <Button
                  onClick={() => setZoomLevel((z) => Math.min(z + 0.1, 3))}
                >
                  <ZoomInOutlined />
                </Button>
                <Button onClick={() => setRotationAngle((r) => r - 90)}>
                  <RotateLeftOutlined />
                </Button>
                <Button onClick={() => setRotationAngle((r) => r + 90)}>
                  <RotateRightOutlined />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <PatientDetailsStageDocument
        handleCancel={handleCancelPreview}
        handleDeleteDocument={handleDeleteList}
        open={previewDocument}
        handleDownloadDocument={handleDownloadDocument}
        // handleSetDocumentUrl={handleSetDocumentUrl}
        documents={Object.values(uploadedDocuments)}
        handleDeficiencyModal={handleDeficiencyModalOpen}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        setDocumentName={setDocumentName}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setUploadedDocuments}
        activeKey={activeKey}
        clickedActiveTab={clickedActiveTab}
        clickedActiveDocumentUrl={clickedActiveDocumentUrl}
      />
    </div>
  );
};

export default RightSideFilePreview;
